import * as React from 'react';
import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Routes, useJourneyNavigation } from '../../../routes';
import { useOrgStore } from '../../../hooks/useOrgStore';
import { LayerRenderEvent } from '../../../layers/LayerManager';
import { LayerWidget } from '../../../layers/LayerWidget';
import { CenterFormOrientation, CenterFormWidget } from '../../../widgets/layout/CenterFormWidget';
import { BreadCrumbWidget } from '../../../widgets/header/BreadCrumbWidget';
import { FormLayoutWidget } from '../../../widgets/layout/FormLayoutWidget';
import { GroupedButtons } from '../../../widgets/buttons/GroupedButtons';
import { IntegrationModel } from '@journeyapps-platform/lib-journey-core';
import { observer } from 'mobx-react';
import { IntegrationKind, IntegrationState } from '@journeyapps-platform/types-cerberus';
import { InstallAzureIntegrationDialogWidget } from './install/InstallAzureIntegrationDialogWidget';
import { useSystem } from '../../../hooks/useSystem';

export interface IntegrationInstallationDialogWidgetProps {}

export const IntegrationInstallationDialogWidget: React.FC<IntegrationInstallationDialogWidgetProps> = observer(
  (props) => {
    const handler = useJourneyNavigation(Routes.INSTALL_INTEGRATION);
    const orgStore = useOrgStore();
    const system = useSystem();
    const { params, navigate } = handler;
    const submitOrCancel = (event: LayerRenderEvent<Routes.INSTALL_INTEGRATION>) => {
      navigate(Routes.MANAGE_INTEGRATIONS, params);
      event.layer.dispose();
    };

    useEffect(() => {
      (async () => {
        const org = await orgStore.resolve(params.org_id);
        await org.getIntegration(params.integration_id!);
      })();
    }, []);

    const integration = orgStore.org(params.org_id)?.integration(params.integration_id);
    if (!integration) {
      return null;
    }

    return (
      <LayerWidget<Routes.INSTALL_INTEGRATION>
        options={{ userCanExit: false, withBackdrop: true }}
        renderLayer={(event) => {
          return (
            <IntegrationInstallation
              integration={integration}
              onComplete={async () => {
                await system.dialogStore.infoDialog({
                  title: 'Installation complete',
                  message: 'The integration was installed successfully.'
                });
                submitOrCancel(event);
              }}
              onCancel={() => {
                submitOrCancel(event);
              }}
            />
          );
        }}
      />
    );
  }
);

const IntegrationInstallation: React.FC<{
  onCancel: () => any;
  onComplete: () => any;
  integration: IntegrationModel;
}> = (props) => {
  const [valid, setValid] = useState(false);
  const [actionHandler, setActionHandler] = useState<{ handler: () => Promise<any> }>();
  if (props.integration.model.state !== IntegrationState.PartiallyConnected) {
    props.onCancel();
    return null;
  }

  return (
    <CenterFormWidget noLogo={true} primaryLabel={'Install Integration'} orientation={CenterFormOrientation.ROW}>
      <BreadCrumbWidget steps={[]} currentStep={0} />
      <FormLayoutWidget
        sections={[
          {
            key: 'integrations',
            elements: [
              props.integration.model.kind === IntegrationKind.AzureDevops ? (
                <InstallAzureIntegrationDialogWidget
                  integration={props.integration}
                  setValid={setValid}
                  setActionHandler={setActionHandler}
                />
              ) : null
            ].filter((f) => !!f)
          },
          {
            key: 'submit',
            elements: [
              <GroupedButtons
                btns={[
                  {
                    label: 'Cancel',
                    action: (event, loading) => {
                      props.onCancel();
                    }
                  },
                  {
                    label: 'Next',
                    disabled: !valid,
                    action: async (event, loading) => {
                      try {
                        loading(true);
                        const success = await actionHandler?.handler();
                        if (success) {
                          props.integration.org().loadIntegrations();
                          props.onComplete();
                        }
                      } finally {
                        loading(false);
                      }
                    }
                  }
                ]}
              />
            ]
          }
        ]}
      />
    </CenterFormWidget>
  );
};

namespace S {
  export const Container = styled.div``;
}
